import cloneDeep from 'lodash/cloneDeep'
import { LoaderFunctionArgs, redirect } from 'react-router-dom'
import { toast } from 'react-toastify'
import { SerializedError } from '@reduxjs/toolkit'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'

import { eventsApi } from '../../../services/events'
import i18n from '../../../i18n'
import { resetEventDetails } from '../../../store.slice'
import type { AppStore } from '../../../store'
import {
  OptionType,
  type Events,
  type Options,
  type Sessions,
  type SubEvents,
} from '../../../types'
import { ModifyEventSchema } from '../../events/Events.schema'

interface ApiMapping<T extends object> {
  data: T
  status: 'success' | 'error'
  error?: FetchBaseQueryError | SerializedError | Error
}

const mapDataWithApiStatus = <T extends object>(
  data: T,
  error?: FetchBaseQueryError | SerializedError | Error
): ApiMapping<T> => ({
  data,
  error,
  status: error ? 'error' : 'success',
})

const requestJsonPerEventsType = async (request: LoaderFunctionArgs['request']) => {
  const data = (await request.json()) as ModifyEventSchema
  if (data.id === -1) {
    return {
      data: data,
      type: 'POST',
    }
  }
  return {
    data: data,
    type: 'PATCH',
  }
}

const createOrModifyEventApi = (data: ModifyEventSchema, store: AppStore) => {
  if (data.id !== -1) {
    return store.dispatch(
      eventsApi.endpoints.modifyEvent.initiate({
        id: data.id,
        data: data.event,
      })
    )
  }
  return store.dispatch(eventsApi.endpoints.createEvent.initiate(data.event))
}

const createOrModifySessionApi = (
  params: {
    session: ModifyEventSchema['sessions'][0]
    event: Events
  },
  store: AppStore
) => {
  const { session: draftSession, event } = params
  const session = cloneDeep(draftSession)
  const shouldCreate = session._new
  // @ts-expect-error - optional prop
  delete session.sub_events
  // @ts-expect-error - optional prop
  delete session._new

  if (shouldCreate) {
    return store.dispatch(
      eventsApi.endpoints.createSession.initiate({
        eventId: event.id,
        session,
      })
    )
  }
  return store.dispatch(
    eventsApi.endpoints.modifySession.initiate({
      eventId: event.id,
      id: session.id,
      data: session,
    })
  )
}

const createOrModifySubEventApi = (
  params: {
    subEvent: ModifyEventSchema['subEvents'][0]
    event: Events
    sessions: [number, ApiMapping<Sessions>][]
  },
  store: AppStore
) => {
  const { subEvent, event, sessions } = params
  const shouldCreate = !!subEvent._new
  // @ts-expect-error - optional prop
  delete subEvent._new

  // Map front session ids to session ids on the server
  // If session assigned was about to be created, use the new session id created in back previously
  subEvent.sessions = subEvent.sessions.map(id => {
    const el = sessions.find(([sessionId]) => sessionId === id)
    if (!!el?.[1]) return el[1].data.id
    return id
  })

  if (shouldCreate) {
    return store.dispatch(
      eventsApi.endpoints.createSubEvent.initiate({
        eventId: event.id,
        subEvent: subEvent,
      })
    )
  }
  return store.dispatch(
    eventsApi.endpoints.modifySubEvent.initiate({
      eventId: event.id,
      id: subEvent.id,
      data: subEvent,
    })
  )
}

const createOrModifyOptionApi = (
  params: {
    option: ModifyEventSchema['options'][0]
    event: Events
    sessions: [number, ApiMapping<Sessions>][]
    subEvents: [number, ApiMapping<SubEvents>][]
  },
  store: AppStore
) => {
  const { option: draftOption, event, sessions, subEvents } = params
  const option = cloneDeep(draftOption)
  const shouldCreate = option._new
  // @ts-expect-error - optional prop
  delete option._new

  // Map front ids to ids on the server
  // If session or subevent assigned was about to be created, use the new id created in back previously
  if (option.type === OptionType.SessionOption) {
    const foundSession = sessions.find(([sessionId]) => sessionId === option.session)
    if (!!foundSession?.[1]) {
      option.session = foundSession[1].data.id
    }
  }
  if (option.type === OptionType.SubEventOption) {
    const foundSubEvent = subEvents.find(([subEventId]) => subEventId === option.sub_event)
    if (!!foundSubEvent?.[1]) {
      option.sub_event = foundSubEvent[1].data.id
    }
  }

  if (shouldCreate) {
    return store.dispatch(
      eventsApi.endpoints.createOption.initiate({
        eventId: event.id,
        option,
      })
    )
  }
  return store.dispatch(
    eventsApi.endpoints.modifyOption.initiate({
      eventId: event.id,
      id: option.id,
      data: option,
    })
  )
}

export const submitEvent =
  (store: AppStore) =>
  async ({ request }: LoaderFunctionArgs) => {
    const { data, type } = await requestJsonPerEventsType(request)
    const { t } = i18n

    // TODO handle picture saving later
    // @ts-expect-error - optional prop
    delete data.event.picture

    let serverError: FetchBaseQueryError | SerializedError | null = null
    let sessions = [] as [number, ApiMapping<Sessions>][]
    let subEvents = [] as [number, ApiMapping<SubEvents>][]
    let options = [] as [number, ApiMapping<Options>][]

    // First save event
    const event = await createOrModifyEventApi(data, store)

    if ('error' in event && event.error) {
      serverError = event.error
      toast.error(t("Erreur lors de la mise à jour de l'événement"))
      return serverError
    }

    // Then save subEvents, sessions and options if success
    if ('data' in event) {
      try {
        // Save sessions
        sessions = await Promise.all(
          data.sessions.map(async session => {
            const sessRes = await createOrModifySessionApi(
              {
                session,
                event: event.data,
              },
              store
            )

            if ('error' in sessRes && sessRes.error) {
              serverError = sessRes.error
              toast.error(
                t('Erreur lors de la mise à jour de la séance {{session}}', {
                  session: session.name,
                })
              )
              return [session.id, mapDataWithApiStatus(session, sessRes.error)]
            }

            if ('data' in sessRes) {
              return [session.id, mapDataWithApiStatus(sessRes.data)]
            }

            return [session.id, mapDataWithApiStatus(session, new Error('Failed to create event'))]
          })
        )

        // If any session failed to create, delete the event
        if (sessions.some(([, session]) => session.status === 'error')) {
          // Delete new created sessions to avoid duplicated data or orphaned data ONLY IF method is PATCH for event
          if (type === 'PATCH') {
            await Promise.all(
              sessions.map(async ([frontId, session]) => {
                const isNew = frontId !== session.data.id
                if (session.status === 'success' && isNew) {
                  await store.dispatch(
                    eventsApi.endpoints.deleteSession.initiate({
                      eventId: event.data.id,
                      id: session.data.id,
                    })
                  )
                }
              })
            )
          }
          throw new Error('Failed to create event')
        }

        // Save subEvents
        subEvents = await Promise.all(
          data.subEvents.map(async subEvent => {
            const subRes = await createOrModifySubEventApi(
              {
                subEvent,
                event: event.data,
                sessions,
              },
              store
            )

            if ('error' in subRes && subRes.error) {
              serverError = subRes.error
              toast.error(
                t("Erreur lors de la mise à jour de l'intervention {{subEvent}}", {
                  subEvent: subEvent.name,
                })
              )
              return [subEvent.id, mapDataWithApiStatus(subEvent, subRes.error)]
            }

            if ('data' in subRes) {
              return [subEvent.id, mapDataWithApiStatus(subRes.data)]
            }

            return [
              subEvent.id,
              mapDataWithApiStatus(subEvent, new Error('Failed to create event')),
            ]
          })
        )

        // If any subEvent failed to create, delete the event
        if (subEvents.some(([, subEvent]) => subEvent.status === 'error')) {
          // Delete new created subEvents to avoid duplicated data or orphaned data ONLY IF method is PATCH for event
          if (type === 'PATCH') {
            await Promise.all(
              subEvents.map(async ([frontId, subEvent]) => {
                const isNew = frontId !== subEvent.data.id
                if (subEvent.status === 'success' && isNew) {
                  await store.dispatch(
                    eventsApi.endpoints.deleteSubEvent.initiate({
                      eventId: event.data.id,
                      id: subEvent.data.id,
                    })
                  )
                }
              })
            )
          }
          throw new Error('Failed to create event')
        }

        // Save options
        options = await Promise.all(
          data.options.map(async option => {
            const optRes = await createOrModifyOptionApi(
              {
                option,
                event: event.data,
                sessions,
                subEvents,
              },
              store
            )

            if ('error' in optRes && optRes.error) {
              serverError = optRes.error
              toast.error(
                t("Erreur lors de la mise à jour de l'option {{option}}", {
                  option: option.name,
                })
              )
              return [option.id, mapDataWithApiStatus(option, optRes.error)]
            }

            if ('data' in optRes) {
              return [option.id, mapDataWithApiStatus(optRes.data)]
            }

            return [option.id, mapDataWithApiStatus(option, new Error('Failed to create event'))]
          })
        )

        // If any subEvent failed to create, delete the event
        if (options.some(([, option]) => option.status === 'error')) {
          // Delete new created options to avoid duplicated data or orphaned data ONLY IF method is PATCH for event
          if (type === 'PATCH') {
            await Promise.all(
              options.map(async ([frontId, option]) => {
                const isNew = frontId !== option.data.id
                if (option.status === 'success' && isNew) {
                  await store.dispatch(
                    eventsApi.endpoints.deleteOption.initiate({
                      eventId: event.data.id,
                      id: option.data.id,
                    })
                  )
                }
              })
            )
          }
          throw new Error('Failed to create event')
        }

        store.dispatch(resetEventDetails(type === 'POST' ? 'new' : String(data.id)))
        toast.success(t('Événement mis à jour avec succès'))

        return redirect(`/events/${event.data.id}`)

        // Check if any error was thrown during the creation of subEvents, sessions or options
        // So delete event if it is POST method
      } catch (error) {
        toast.error(t("Erreur lors de la mise à jour de l'événement"))

        if (type === 'POST') {
          await store.dispatch(eventsApi.endpoints.deleteEvent.initiate(event.data.id))
        }
        return serverError
      }
    }

    // If any unhandled and unexpected error was thrown
    toast.error(t("Erreur lors de la mise à jour de l'événement"))
    return serverError
  }
